import ImagePreview from '@/components/forms/imagePreview';
import { Upload } from '@/components/upload';
import { useAttributes } from '@/hooks/useAttributes';
import { useDynamicLocale } from '@/hoslog-intl';
import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { BusinessInfo } from './form/BusinessInfo';
import { Documents } from './form/Document';

interface BasicInfoProps {
  premiumStoreId?: string;
  businessTypeFromApi?: string;
  identificationTypeFromApi?: string;
  profileImagePath?: string | undefined;
  setProfileImage?: (image: string) => void;
  coverImagePath?: string | undefined;
  setCoverImage?: (image: string) => void;
  kycFrontImage?: string | undefined;
  setKycFrontImage?: (image: string) => void;
  kycBackImage?: string | undefined;
  setKycBackImage?: (image: string) => void;
  passportImagePath?: string | undefined;
  setPassportImage?: (image: string) => void;
  insuranceImagePath?: string | undefined;
  setInsuranceImage?: (image: string) => void;
  businessImagePath?: string | undefined;
  setBusinessImage?: (image: string) => void;
  corporateRegistryImagePath?: string | undefined;
  setCorporateRegistryImage?: (image: string) => void;
}

export type BusinessFormValues = {
  name?: string;
  businessType?: string;
  storeName?: string;
  storeNameFurigana?: string;
  groupName?: string;
  description?: string;
  ceoFullName?: string;
  ceoFurigana?: string;
  companyName?: string;
  companyNameFurigana?: string;
  companyAddress?: string;
  landlineNumber?: string;
  mobileNumber?: string;
  emailAddress?: string;
  username?: string;
  profileImage?: File | string;
  coverImage?: File | string;
};
export const BasicInfo: React.FC<BasicInfoProps> = ({
  premiumStoreId,
  businessTypeFromApi,
  identificationTypeFromApi,
  profileImagePath,
  setProfileImage = () => {},
  coverImagePath,
  setCoverImage = () => {},
  passportImagePath,
  setPassportImage = () => {},
  kycFrontImage,
  setKycFrontImage = () => {},
  kycBackImage,
  setKycBackImage = () => {},
  insuranceImagePath,
  setInsuranceImage = () => {},
  businessImagePath,
  setBusinessImage = () => {},
  corporateRegistryImagePath,
  setCorporateRegistryImage = () => {},
}) => {
  const { control, setValue } = useFormContext<BusinessFormValues>();
  const { t } = useDynamicLocale();
  const selectedBusinessType = useWatch({ control, name: 'businessType' });

  const { data: identificationTypeOptions = [] } = useAttributes('identificationType') || {};

  return (
    <div className="flex flex-col w-full space-y-6">
      <div className="mt-8 relative">
        {coverImagePath ? (
          <span>
            <ImagePreview
              imageClassName="w-full"
              imageSrc={`${coverImagePath}?v=${Date.now()}`}
              alt="coverImage"
              cancelClickHandler={() => {
                setCoverImage('');
                setValue('coverImage', '');
              }}
            />
          </span>
        ) : (
          <Controller
            name="coverImage"
            control={control}
            rules={{ required: 'Please upload a profile picture' }}
            render={({ field, fieldState }) => (
              <Upload
                maxSizeMB={5}
                accept=".jpg,.jpeg,.png,.svg,.heic,.heif"
                multiple={false}
                buttonText={t('common.page.button.browseFiles')}
                previewClassName="h-52 w-full p-0 "
                error={fieldState.error?.message}
                onFilesChange={(files) => {
                  return field.onChange(files?.[0] ?? '');
                }}
                dropZoneClassName="relative w-full inline-flex items-center justify-center bg-surface-subtle min-h-52"
              />
            )}
          />
        )}
        {profileImagePath ? (
          <ImagePreview
            className="absolute top-23 left-10"
            variant="circle"
            imageClassName="rounded-full"
            imageSrc={`${profileImagePath}?v=${Date.now()}`}
            alt="profileImage"
            cancelClickHandler={() => {
              setProfileImage('');
              setValue('profileImage', '');
            }}
          />
        ) : (
          <Controller
            name="profileImage"
            control={control}
            rules={{ required: 'Please upload a cover picture' }}
            render={({ field, fieldState }) => (
              <Upload
                maxSizeMB={5}
                accept=".jpg,.jpeg,.png,.svg,.heic,.heif"
                multiple={false}
                error={fieldState.error?.message}
                onFilesChange={(files) => {
                  return field.onChange(files?.[0] ?? '');
                }}
                variant="circle"
                className="absolute bottom-0 left-[48px] translate-y-1/2"
                showPlaceholderText={false}
              />
            )}
          />
        )}
      </div>
      <BusinessInfo
        premiumStoreId={premiumStoreId}
        selectedBusinessType={selectedBusinessType}
        businessTypeFromApi={businessTypeFromApi}
      />
      <Documents
        businessTypeFromApi={businessTypeFromApi}
        identificationTypeFromApi={identificationTypeFromApi}
        selectedBusinessType={selectedBusinessType}
        passportImagePath={passportImagePath}
        identificationTypeOptions={identificationTypeOptions}
        setPassportImage={setPassportImage}
        kycFrontImage={kycFrontImage}
        setKycFrontImage={setKycFrontImage}
        kycBackImage={kycBackImage}
        setKycBackImage={setKycBackImage}
        insuranceImagePath={insuranceImagePath}
        setInsuranceImage={setInsuranceImage}
        businessImagePath={businessImagePath}
        setBusinessImage={setBusinessImage}
        corporateRegistryImagePath={corporateRegistryImagePath}
        setCorporateRegistryImage={setCorporateRegistryImage}
      />
    </div>
  );
};
